import { Injectable } from '@angular/core';
import { Plugin } from '../shared/plugin.interface';

/**
 * Maps plugin IDs (received from the `frontend-configuration` API) to
 * lazy-loaded plugin definitions in the frontend codebase.
 *
 * This is the bridge between "the backend says this tenant uses bw"
 * and "the frontend loads the BW_WEB_PLUGIN object".
 *
 * Unknown plugin IDs are logged and skipped — the app continues without them.
 *
 * @example
 * ```typescript
 * const loader = inject(WebPluginLoaderService);
 * const plugins = await loader.loadPlugins(['bw']);
 * // → [BW_WEB_PLUGIN]
 * ```
 */
@Injectable({ providedIn: 'root' })
export class WebPluginLoaderService {
  private readonly logger = console;

  /**
   * Maps API `plugin_id` strings to lazy-loaded plugin definitions.
   *
   * Add new entries here when creating a new web plugin.
   * The dynamic imports ensure that plugin code is only bundled
   * when actually used (tree-shaking + code splitting).
   */
  private readonly pluginMap: Record<string, () => Promise<Plugin>> = {
    bw: () => import('@tourfold/plugins/bw').then(m => m.BW_WEB_PLUGIN),
    // Future plugins:
    // oeamtc: () => import('@tourfold/plugins/oeamtc').then(m => m.OEAMTC_WEB_PLUGIN),
  };

  /**
   * Load all plugins by their IDs.
   *
   * @param pluginIds - Canonical plugin IDs from the API (`plugins`)
   * @returns Array of loaded plugin instances (unknown IDs are skipped)
   */
  async loadPlugins(pluginIds: string[]): Promise<Plugin[]> {
    const plugins: Plugin[] = [];

    for (const id of pluginIds) {
      const loader = this.pluginMap[id];
      if (!loader) {
        this.logger.warn(`[WebPluginLoader] Unknown plugin ID: '${id}', skipping`);
        continue;
      }

      try {
        const plugin = await loader();
        plugins.push(plugin);
      } catch (error) {
        this.logger.error(`[WebPluginLoader] Failed to load plugin '${id}':`, error);
      }
    }

    return plugins;
  }
}
